'use client'

import { BaseLink } from '@/components/Core'
import { BaseLinkProps } from '@/components/Core/types'
import { LinkFragment } from '@/lib/fragments'
import { getLinkTitle, getLinkUrl } from '@/lib/slugs'
import { Lib } from '@/types'
import { Icon } from '@iconify-icon/react'
import clsx from 'clsx'
import { useLocale } from 'next-intl'
import { usePathname } from 'next/navigation'
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import { Button as AriaButton } from 'react-aria-components'
import { tv } from 'tailwind-variants'

export interface ButtonProps extends BaseLinkProps {
  children?: React.ReactNode
  variant?: 'default' | 'outline' | 'ghost' | 'text'
  size?: 'base' | 'small' | 'large'
  disabled?: boolean
  icon?: string
  color?: 'default' | 'invert' | 'brand'
  iconPosition?: 'left' | 'right'
  iconAnimation?: 'rotate' | 'move-left' | 'move-right' | 'none'
  activeState?: boolean
  isCurrent?: boolean
  type?: 'button' | 'submit' | 'reset'
  ariaWrapper?: boolean
  preventSameUrlClass?: string
  data?: Lib.FragmentOf<typeof LinkFragment> | null
}

export const buttonVariants = ['fill', 'outline', 'ghost', 'text']

const buttonClasses = tv({
  base: 'text-center transition border-2 font-semibold min-h-10 px-5 rounded-full leading-[1] inline-flex items-center justify-center',
  variants: {
    type: {
      default:
        'bg-[var(--color-bg)] text-[var(--color-foreground)] border-[var(--color-border)]',
      outline:
        'text-[var(--color-bg)] border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]',
      ghost:
        'bg-[var(--color-bg)]/20 text-[var(--color-bg)] border-transparent [.isActive]:border-[var(--color-border)] backdrop-blur-sm',
      text: 'px-0 border-none'
    },
    color: {
      default:
        '[--color-bg:var(--color-white)] [--color-foreground:var(--color-black)] [--color-border:var(--color-black)]',
      invert:
        '[--color-bg:var(--color-white)] [--color-foreground:var(--color-black)] [--color-border:var(--color-white)]',
      brand:
        '[--color-bg:var(--color-primary-300)] [--color-foreground:var(--color-yellow)] [--color-border:var(--color-primary-300)]'
    },
    size: {
      base: '',
      small: 'text-xs',
      large: 'text-base'
    }
  },
  defaultVariants: {
    size: 'base',
    type: 'default',
    color: 'default'
  }
})

const iconAnimationClasses = {
  rotate: 'transition-transform group-hover:rotate-90',
  'move-left': 'transition-transform group-hover:-translate-x-1',
  'move-right': 'transition-transform group-hover:translate-x-1',
  none: ''
}

const Button = forwardRef<HTMLAnchorElement, ButtonProps>(
  (
    {
      className,
      children,
      variant = 'default',
      size = 'base',
      icon,
      color = 'default',
      iconPosition = 'right',
      iconAnimation = 'move-right',
      activeState,
      ariaWrapper,
      data,
      preventSameUrlClass = '',
      ...props
    },
    ref
  ) => {
    const comp = useRef<any>(null)
    const [sameUrl, setSameUrl] = useState(false)

    useImperativeHandle(ref, () => comp.current as HTMLAnchorElement)

    const url = getLinkUrl({ data })
    const title = getLinkTitle({ data })

    const pathname = usePathname()
    const locale = useLocale()
    useEffect(() => {
      if (!url || !pathname) return
      const fullUrl = `/${locale}${url}`
      setSameUrl(pathname === fullUrl)
    }, [pathname, url, locale, preventSameUrlClass])

    const classNames = clsx(
      {
        'opacity-50 pointer-events-none': props.disabled,
        isActive:
          activeState ||
          (data?.url && 'slug' in data.url
            ? pathname.split('/').includes(data.url.slug)
            : false)
      },
      buttonClasses({
        type: variant,
        size: size,
        color: color
      }),
      className,
      sameUrl && preventSameUrlClass
    )

    const iconClasses = clsx(
      'inline-block',
      iconPosition === 'left' ? 'mr-2 order-first' : 'ml-2 order-last',
      iconAnimation && iconAnimationClasses[iconAnimation]
    )

    const getHref = () => {
      if (data?.isExternal) return data?.externalUrl
      else if (url) return url
      else return props.href
    }

    const Content = () => {
      return (
        <>
          {icon && iconPosition === 'left' && (
            <Icon
              width="1em"
              height="1em"
              icon={icon}
              className={iconClasses}
            />
          )}
          <span>
            {
              (data?.titleText && title
                ? title
                : data?.text || children) as React.ReactNode
            }
          </span>
          {icon && iconPosition === 'right' && (
            <Icon
              width="1em"
              height="1em"
              icon={icon}
              className={iconClasses}
            />
          )}
        </>
      )
    }

    const Component = () => (
      <BaseLink
        ref={comp}
        {...props}
        className={clsx('group/button', classNames)}
        isNext={data?.isExternal || props.isNext}
        target={
          data?.targetBlank ? '_blank' : props.target ? props.target : undefined
        }
        href={getHref() || undefined}
      >
        <span className="relative overflow-hidden py-1">
          <span className="opacity-0">
            <Content />
          </span>
          <span className="absolute inset-0 flex flex-col items-center justify-center transition-all group-hover/button:-translate-y-full">
            <span className="pointer-events-none flex items-center justify-center">
              <Content />
            </span>
            <span className="absolute top-full flex h-full w-full items-center justify-center">
              <Content />
            </span>
          </span>
        </span>
      </BaseLink>
    )

    return ariaWrapper ? (
      <AriaButton>
        <Component />
      </AriaButton>
    ) : (
      <Component />
    )
  }
)

Button.displayName = 'Button'

export default memo(Button)
